import * as Yup from 'yup';
import PropTypes from 'prop-types';
import React from 'react';
import { useBoolean } from 'usehooks-ts';
import { useFormik } from 'formik';
import { useLocation, Link, useHistory } from 'react-router-dom';

import Breadcrumb from 'components/App/Breadcrumb';
import Button from 'components/App/Button';
import Card from 'components/App/Card';
import FormCreateAccountPromotion from 'components/FormCreateAccountPromotion';
import ModalPromotionSummarize from 'components/Modals/ModalPromotionSummarize';
import ModalSuccess from 'components/Modals/ModalSuccess';

import INITIAL_VALUE from 'constants/initialValue';
import { LOCATIONS, LOGIN_TYPE, storageKey, themeColor } from 'constants/index';
import { PROMOTION_CATEGORY, PROMOTION_TYPE } from 'constants/promotion';
import cn from 'utils/classnames';
import { parseCurrency } from 'utils';
import { createPromotionValidation } from 'utils/validate';
import { ReactComponent as GuestPromotionIcon } from 'assets/images/guest-promotion.svg';

import styles from './CreateTouristPromotion.module.scss';

function CreateTouristPromotion(props) {
  const {
    isLoadingCreatePlacePromotion,
    placePromotionDetail,
    actionGetPlacePromotionDetail,
    actionCreatePlacePromotion,
    actionClearDataPlacePromotionDetail,
  } = props;

  const history = useHistory();
  const location = useLocation();
  const placeId = new URLSearchParams(location.search).get('place');
  const promotionId = new URLSearchParams(location.search).get('promotion');

  const isExtranet = React.useMemo(() =>
    localStorage.getItem(storageKey.LOGIN_TYPE) === LOGIN_TYPE.EXTRANET,
  []);

  const breadcrumbList = React.useMemo(() => [
    {
      name: 'Chương trình khuyến mãi',
      isActive: false,
      link: '',
    },
    {
      name: 'Tạo chương trình khuyến mãi',
      isActive: false,
      link: placeId
        ? `${LOCATIONS.LOCATION_DETAIL.CREATE_PROMOTION}?place=${placeId}`
        : LOCATIONS.LOCATION_DETAIL.CREATE_PROMOTION,
    },
    {
      name: 'Tạo Ưu Đãi Du khách',
      isActive: true,
      link: '',
    },
  ], [placeId]);

  const {
    value: isOpenModal,
    setTrue: onOpenModal,
    setFalse: onCloseModal,
  } = useBoolean(false);

  const {
    value: isOpenSuccessModal,
    setTrue: onOpenSuccessModal,
    setFalse: onCloseSuccessModal,
  } = useBoolean(false);

  const validation = useFormik({
    initialValues: {
      name: '',
      discountRate: '',
      isAllRooms: true,
      isAllPriceTypes: true,
    },

    validationSchema: Yup.object({
      name: createPromotionValidation.name,
      discountRate: createPromotionValidation.discountRate,
    }),

    onSubmit: () => {
      onOpenModal();
    },
  });

  const buildPromotionPayload = React.useCallback(
    () => ({
      place: placeId,
      name: validation.values.name,
      category: PROMOTION_CATEGORY.ACCOUNT,
      type: PROMOTION_TYPE.TOURIST,
      discount: parseCurrency(validation.values.discountRate),
    }),
    [placeId, validation.values.discountRate, validation.values.name],
  );

  const onCallback = React.useCallback(() => {
    onCloseModal();
    onOpenSuccessModal();
  }, [onCloseModal, onOpenSuccessModal]);

  const handleCloseSuccessModal = React.useCallback(() => {
    onCloseSuccessModal();
    history.push({
      pathname: LOCATIONS.LOCATION_DETAIL.PROMOTION,
      search: `?place=${placeId}&status=PENDING`,
    });
  }, [placeId, history, onCloseSuccessModal]);

  const onSubmitCreateTouristPromotion = React.useCallback(() => {
    const promotion = buildPromotionPayload();

    actionCreatePlacePromotion({
      promotionId,
      promotion,
      callback: onCallback,
    });
  }, [
    actionCreatePlacePromotion,
    buildPromotionPayload,
    onCallback,
    promotionId,
  ]);

  React.useEffect(() => {
    if (promotionId && placeId) {
      actionGetPlacePromotionDetail({ placeId, promotionId });
    }

    return () => {
      actionClearDataPlacePromotionDetail();
    };
  }, [promotionId, placeId]);

  React.useEffect(() => {
    if (Object.keys(placePromotionDetail).length > 0) {
      validation.setValues({
        name: placePromotionDetail.name || '',
        discountRate: placePromotionDetail.discount ?? '',
        isAllRooms: true,
        isAllPriceTypes: true,
      });
    }

    return () => {
      validation.resetForm();
    };
  }, [placePromotionDetail]);

  return (
    <div className={cn('page-content', styles.Page)}>
      <Breadcrumb pageTitle="Tạo ưu đãi du khách" breadcrumbList={breadcrumbList} />

      <div className={styles.Wrapper}>
        <Card className={styles.Section}>
          <div className={styles.SectionTop}>
            <GuestPromotionIcon className={styles.Icon} />

            <div className={styles.SectionTopTitle}>
              <h3>Tạo Ưu Đãi Du khách</h3>
              <p>Ưu Đãi dành cho Tài khoản có 5 đặt phòng/năm</p>
            </div>
          </div>
        </Card>

        <FormCreateAccountPromotion
          validation={validation}
          // roomOfPlaceList={roomOfPlaceList}
          // placePriceTypes={placePriceTypes}
        />

        <div className={isExtranet ? styles.StickyButtonsExtranet : styles.StickyButtons}>
          <div className={styles.ButtonWrap}>
            <Link
              to={{
                pathname: LOCATIONS.LOCATION_DETAIL.PROMOTION,
                search: `?place=${placeId}`,
              }}
            >
              <Button color={themeColor.SECONDARY}>Hủy</Button>
            </Link>

            <Button
              onClick={validation.handleSubmit}
              disabled={!!placePromotionDetail.deletedAt}
            >
              {promotionId ? 'Cập nhật' : 'Tạo'}
            </Button>
          </div>
        </div>

        <ModalPromotionSummarize
          isOpen={isOpenModal}
          loading={isLoadingCreatePlacePromotion}
          validation={validation}
          isSimpleSummary
          onCloseClick={onCloseModal}
          onSubmit={onSubmitCreateTouristPromotion}
        />

        <ModalSuccess
          isOpen={isOpenSuccessModal}
          onCloseModal={handleCloseSuccessModal}
          message={promotionId ? 'Bạn đã cập nhật khuyến mãi thành công' : 'Bạn đã tạo khuyến mãi thành công'}
        />
      </div>
    </div>
  );
}

CreateTouristPromotion.propTypes = {
  isLoadingCreatePlacePromotion: PropTypes.bool.isRequired,
  placePromotionDetail: PropTypes.instanceOf(Object),
  actionGetPlacePromotionDetail: PropTypes.func.isRequired,
  actionCreatePlacePromotion: PropTypes.func.isRequired,
  actionClearDataPlacePromotionDetail: PropTypes.func.isRequired,
};

CreateTouristPromotion.defaultProps = {
  placePromotionDetail: INITIAL_VALUE.OBJECT,
};

export default CreateTouristPromotion;
